/**
 * /master/content — AI Content Agent dashboard for Super Admin.
 *
 * Lets the master generate articles via GPT / Claude 4.5 / Gemini 3 Pro,
 * pick a niche (or rotate from a preset), seed with an optional source URL,
 * and publish/unpublish/delete drafts.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2, Sparkles, Eye, EyeOff, Trash2, ExternalLink, Plus, FileText,
  Calendar, Bot, Tag, RefreshCcw, Send, ChevronDown,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NICHE_PRESETS = [
  "AI in B2B Sales",
  "Lead Generation for Local Businesses",
  "Sales Automation for Service Companies",
  "HVAC & Home Services Marketing",
  "Construction & Maintenance Lead Acquisition",
  "Marketing for Lawyers & Professional Services",
  "Real Estate Lead Gen",
  "Outbound Cold Email Trends",
];

const STATUS_BADGE = {
  published: { label: "Publicado", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  draft:     { label: "Rascunho",  color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  archived:  { label: "Arquivado", color: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return "—"; }
}

export default function MasterContent() {
  const [models, setModels] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  // Form state
  const [model, setModel] = useState("gpt-4o");
  const [niche, setNiche] = useState(NICHE_PRESETS[0]);
  const [customNiche, setCustomNiche] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceHeadline, setSourceHeadline] = useState("");
  const [autoPublish, setAutoPublish] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [genSuccess, setGenSuccess] = useState("");
  const [openAdvanced, setOpenAdvanced] = useState(false);

  // Load models once
  useEffect(() => {
    axios.get(`${API}/blog/admin/models`)
      .then(({ data }) => setModels(data.models || []))
      .catch(() => {});
  }, []);

  // Load posts (re-fetch on filter or refresh)
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = filterStatus ? `?status=${filterStatus}` : "";
    axios.get(`${API}/blog/admin/posts${params}`)
      .then(({ data }) => { if (alive) setPosts(data.posts || []); })
      .catch(() => { if (alive) setPosts([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filterStatus, refreshTick]);

  const stats = useMemo(() => {
    const total = posts.length;
    const pub = posts.filter((p) => p.status === "published").length;
    const drafts = posts.filter((p) => p.status === "draft").length;
    const words = posts.reduce((acc, p) => acc + (p.word_count || 0), 0);
    return { total, pub, drafts, words };
  }, [posts]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenError(""); setGenSuccess("");
    const finalNiche = (customNiche || niche || "").trim();
    if (!finalNiche) { setGenError("Informe um nicho/tema."); return; }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/blog/admin/generate`, {
        model, niche: finalNiche,
        source_url: sourceUrl || null,
        source_headline: sourceHeadline || null,
        auto_publish: autoPublish,
      });
      setGenSuccess(`✓ Artigo gerado: "${data.post.title}" (${data.post.word_count} palavras)`);
      setSourceUrl(""); setSourceHeadline(""); setCustomNiche("");
      setRefreshTick((t) => t + 1);
    } catch (err) {
      setGenError(err?.response?.data?.detail || err.message || "Erro ao gerar artigo");
    } finally {
      setGenerating(false);
    }
  };

  const handlePublish = async (post) => {
    const ep = post.status === "published" ? "unpublish" : "publish";
    try {
      await axios.post(`${API}/blog/admin/posts/${post.id}/${ep}`);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Excluir "${post.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/blog/admin/posts/${post.id}`);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  return (
    <MasterLayout title="Content AI — Blog Generator">
      <div data-testid="master-content-page" className="space-y-8">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "total", label: "Total de posts", value: stats.total, icon: FileText, color: "#C9A24E" },
            { k: "pub",   label: "Publicados",     value: stats.pub,   icon: Eye,      color: "#10B981" },
            { k: "drafts",label: "Rascunhos",      value: stats.drafts,icon: EyeOff,   color: "#F59E0B" },
            { k: "words", label: "Total palavras", value: stats.words, icon: Sparkles, color: "#8B5CF6" },
          ].map((kpi) => {
            const Ico = kpi.icon;
            return (
              <div key={kpi.k} data-testid={`master-content-kpi-${kpi.k}`}
                className="rounded-xl p-5 border"
                style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(201,162,78,0.18)" }}>
                <Ico className="w-5 h-5 mb-3" style={{ color: kpi.color }} />
                <div className="text-3xl font-extrabold text-white">{kpi.value}</div>
                <div className="text-[11.5px] uppercase tracking-wider text-white/55 mt-1">{kpi.label}</div>
              </div>
            );
          })}
        </div>

        {/* Generation form */}
        <form onSubmit={handleGenerate} data-testid="master-content-generate-form"
          className="rounded-xl p-6 border"
          style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(201,162,78,0.18)" }}>
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-5 h-5 text-[#C9A24E]" />
            <h2 className="text-lg font-bold text-white">Gerar novo artigo</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">Modelo IA</label>
              <select value={model} onChange={(e) => setModel(e.target.value)}
                data-testid="master-content-model-select"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white focus:border-[#C9A24E] focus:outline-none">
                {models.map((m) => (
                  <option key={m.key} value={m.key} style={{ background: "#0F0820" }}>
                    {m.label} — {m.vibe}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">Nicho preset</label>
              <select value={niche} onChange={(e) => setNiche(e.target.value)}
                data-testid="master-content-niche-select"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white focus:border-[#C9A24E] focus:outline-none">
                {NICHE_PRESETS.map((n) => (
                  <option key={n} value={n} style={{ background: "#0F0820" }}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">
              Nicho customizado <span className="font-normal lowercase tracking-normal text-white/35">(opcional, sobrescreve o preset)</span>
            </label>
            <input value={customNiche} onChange={(e) => setCustomNiche(e.target.value)}
              data-testid="master-content-custom-niche"
              placeholder='Ex: "Impermeabilização para residências em SP"'
              className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white placeholder-white/30 focus:border-[#C9A24E] focus:outline-none" />
          </div>

          <button type="button" onClick={() => setOpenAdvanced((v) => !v)}
            data-testid="master-content-advanced-toggle"
            className="text-[12px] text-[#C9A24E] hover:text-white inline-flex items-center gap-1 mb-3">
            <ChevronDown className={`w-4 h-4 transition-transform ${openAdvanced ? "rotate-180" : ""}`} />
            Modo Híbrido (seed com notícia real)
          </button>

          {openAdvanced && (
            <div className="grid md:grid-cols-2 gap-4 mb-4 rounded-lg bg-white/[0.02] border border-white/10 p-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">URL da notícia</label>
                <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)}
                  data-testid="master-content-source-url"
                  placeholder="https://forbes.com/..."
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white placeholder-white/30 focus:border-[#C9A24E] focus:outline-none" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">Manchete da notícia</label>
                <input value={sourceHeadline} onChange={(e) => setSourceHeadline(e.target.value)}
                  data-testid="master-content-source-headline"
                  placeholder="AI is reshaping B2B sales..."
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-[13px] text-white placeholder-white/30 focus:border-[#C9A24E] focus:outline-none" />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-[13px] text-white/80 cursor-pointer">
              <input type="checkbox" checked={autoPublish} onChange={(e) => setAutoPublish(e.target.checked)}
                data-testid="master-content-auto-publish"
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#C9A24E]" />
              Publicar automaticamente em <span className="text-[#C9A24E] font-bold">/blog</span>
            </label>
            <button type="submit" disabled={generating}
              data-testid="master-content-generate-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-extrabold uppercase tracking-tight text-[12.5px] disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
              style={{ background: "#C9A24E", color: "#0F0820", boxShadow: "0 8px 30px -10px rgba(201,162,78,0.55)" }}>
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {generating ? "Gerando…" : "Gerar artigo"}
            </button>
          </div>

          {genError && <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm" data-testid="master-content-error">{genError}</div>}
          {genSuccess && <div className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm" data-testid="master-content-success">{genSuccess}</div>}
        </form>

        {/* Posts table */}
        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-bold text-white">Artigos</h2>
            <div className="flex items-center gap-2">
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="master-content-filter"
                className="bg-white/5 border border-white/15 rounded-md px-3 py-1.5 text-[12.5px] text-white focus:border-[#C9A24E] focus:outline-none">
                <option value="" style={{ background: "#0F0820" }}>Todos</option>
                <option value="published" style={{ background: "#0F0820" }}>Publicados</option>
                <option value="draft" style={{ background: "#0F0820" }}>Rascunhos</option>
              </select>
              <button onClick={() => setRefreshTick((t) => t + 1)}
                data-testid="master-content-refresh"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/15 text-[12px] text-white/70 hover:bg-white/5">
                <RefreshCcw className="w-3.5 h-3.5" /> Atualizar
              </button>
            </div>
          </div>

          {loading && (
            <div className="text-white/60 flex items-center gap-2" data-testid="master-content-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando…
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div className="rounded-xl border p-10 text-center" data-testid="master-content-empty"
              style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.02)" }}>
              <FileText className="w-10 h-10 text-[#C9A24E] mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white mb-1">Nenhum artigo ainda</h3>
              <p className="text-white/55 text-sm">Gere o primeiro acima — o cron automático roda toda terça e sexta.</p>
            </div>
          )}

          {!loading && posts.length > 0 && (
            <div className="rounded-xl border overflow-hidden"
              style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.02)" }}>
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b text-white/55 text-[11px] uppercase tracking-wider"
                    style={{ borderColor: "rgba(201,162,78,0.18)" }}>
                    <th className="text-left px-4 py-3 font-bold">Título</th>
                    <th className="text-left px-4 py-3 font-bold">Status</th>
                    <th className="text-left px-4 py-3 font-bold">Modelo</th>
                    <th className="text-left px-4 py-3 font-bold">Palavras</th>
                    <th className="text-left px-4 py-3 font-bold">Criado</th>
                    <th className="text-right px-4 py-3 font-bold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((p) => {
                    const badge = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                    return (
                      <tr key={p.id} data-testid={`master-content-row-${p.slug}`}
                        className="border-b last:border-b-0 hover:bg-white/[0.03] transition-colors"
                        style={{ borderColor: "rgba(201,162,78,0.1)" }}>
                        <td className="px-4 py-3">
                          <div className="font-bold text-white text-[13.5px] mb-0.5 max-w-md truncate">{p.title}</div>
                          <div className="text-[11px] text-white/45 inline-flex items-center gap-2">
                            <Tag className="w-3 h-3" /> {p.niche || "—"}
                            <span className="text-white/25">•</span>
                            <span className="font-mono">/{p.slug}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase border ${badge.color}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white/70 inline-flex items-center gap-1.5"><Bot className="w-3 h-3 text-[#C9A24E]" /> {p.model}</td>
                        <td className="px-4 py-3 text-white/70">{p.word_count || 0}</td>
                        <td className="px-4 py-3 text-white/55 inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatDate(p.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5 justify-end">
                            {p.status === "published" && (
                              <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer"
                                data-testid={`master-content-view-${p.slug}`}
                                className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white" title="Ver no site">
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                            <button onClick={() => handlePublish(p)}
                              data-testid={`master-content-toggle-${p.slug}`}
                              className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white"
                              title={p.status === "published" ? "Despublicar" : "Publicar"}>
                              {p.status === "published" ? <EyeOff className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                            </button>
                            <button onClick={() => handleDelete(p)}
                              data-testid={`master-content-delete-${p.slug}`}
                              className="p-1.5 rounded-md hover:bg-red-500/20 text-white/55 hover:text-red-300"
                              title="Excluir">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-xl p-5 border text-[12.5px] text-white/55"
          style={{ background: "rgba(201,162,78,0.06)", borderColor: "rgba(201,162,78,0.25)" }}>
          <div className="flex items-center gap-2 text-[#C9A24E] font-bold mb-1.5">
            <Bot className="w-4 h-4" /> Cron automático
          </div>
          O Content Agent publica novos artigos automaticamente toda <strong className="text-white">terça e sexta às 09:00 UTC</strong>,
          rotacionando entre os 6 nichos preset e os 3 modelos LLM. Você recebe e-mail de notificação a cada publicação.
        </div>
      </div>
    </MasterLayout>
  );
}
